import { Box, Button, Collapse, Flex, Heading, Link, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

export const DATAROOM_INGEST_EMAIL = 'dataroom@twelvegrow.com';

function ExtractOption({ step, title, children, action }) {
  return (
    <Flex
      gap={3}
      p={4}
      borderWidth="1px"
      borderColor="secondaryGray.200"
      borderRadius="lg"
      align="flex-start"
      wrap="wrap"
    >
      <Flex
        align="center"
        justify="center"
        minW="28px"
        h="28px"
        borderRadius="full"
        bg="purple.50"
        color="purple.700"
        fontSize="sm"
        fontWeight={700}
      >
        {step}
      </Flex>
      <Box flex="1" minW="220px">
        <Text fontWeight="semibold">{title}</Text>
        <Box mt={1} fontSize="sm" color="secondaryGray.600">
          {children}
        </Box>
      </Box>
      {action ? <Box flexShrink={0}>{action}</Box> : null}
    </Flex>
  );
}

ExtractOption.propTypes = {
  step: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  action: PropTypes.node,
};

export default function ExtractSourceOptions({
  onRunManualExtract,
  reExtracting,
  forceChatOpen = false,
  dragActive = false,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
  children,
}) {
  const [chatOpen, setChatOpen] = useState(Boolean(forceChatOpen));

  useEffect(() => {
    if (forceChatOpen) {
      setChatOpen(true);
    }
  }, [forceChatOpen]);

  return (
    <Box>
      <Heading variant="sectionHeader" borderColor="secondaryGray.600" mb={3}>
        How to extract
      </Heading>
      <Text fontSize="sm" color="secondaryGray.600" mb={4}>
        Three ways to load accounting and cohort files into this customer&apos;s snapshot.
        Create scores from the latest saved extract.
      </Text>
      <Flex direction="column" gap={3}>
        <ExtractOption
          step={1}
          title="Run the manual extractor"
          action={
            <Button
              size="sm"
              colorScheme="purple"
              onClick={onRunManualExtract}
              isLoading={reExtracting}
            >
              Run extractor
            </Button>
          }
        >
          Pulls files from this customer&apos;s Google Drive data folder and extracts accounting
          and cohort data.
        </ExtractOption>

        <ExtractOption step={2} title="Email ingestion">
          Send files to{' '}
          <Link href={`mailto:${DATAROOM_INGEST_EMAIL}`} color="brand.500">
            {DATAROOM_INGEST_EMAIL}
          </Link>{' '}
          (To, Cc, or Bcc). Attachments are extracted automatically for the matched customer.
          Reload this page after the email is processed to see the new snapshot.
        </ExtractOption>

        <ExtractOption
          step={3}
          title="Chat"
          action={
            <Button size="sm" variant="outline" onClick={() => setChatOpen((open) => !open)}>
              {chatOpen ? 'Hide chat' : 'Open chat'}
            </Button>
          }
        >
          Ask questions, upload files, or pick from Drive in chat. Collapsed by default to keep
          this page short.
        </ExtractOption>
      </Flex>

      <Collapse in={chatOpen} unmountOnExit>
        <Box
          className={dragActive ? 'drag-active' : ''}
          mt={4}
          border="1px solid"
          borderColor="purple.100"
          borderRadius="lg"
          p={4}
          bg="white"
          position="relative"
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

ExtractSourceOptions.propTypes = {
  onRunManualExtract: PropTypes.func.isRequired,
  reExtracting: PropTypes.bool,
  forceChatOpen: PropTypes.bool,
  dragActive: PropTypes.bool,
  onDragEnter: PropTypes.func,
  onDragOver: PropTypes.func,
  onDragLeave: PropTypes.func,
  onDrop: PropTypes.func,
  children: PropTypes.node,
};
